import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDeleteFileByIdBo, useUpdateFileBo } from "@/api/file.api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Modal } from "@/components/ui/customs/modal";
import { FileAction, getCreatableFileTypes } from "@/constants/fileCrud";
import { LinkFileModalProps } from "../../expeditionForm.types";
import { ExpeditionDocumentCard } from "../../expeditionDocumentCard";
import { UploadFileBoModal } from "../../uploadFileModal";
import { filterDocumentsExceptCustomInvoice } from "./document.utils";

export const LinkFileModal = ({ open, onClose, expedition }: LinkFileModalProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const documents = expedition.expeditionDocuments?.documents ?? [];
  const documentsExceptCustomInvoice = filterDocumentsExceptCustomInvoice(documents);
  const sendViaApiSnapshotKey = documentsExceptCustomInvoice
    .map((d) => `${d.id}:${d.sendViaApi}`)
    .join("|");

  const queryClient = useQueryClient();
  const { mutateAsync: updateFile } = useUpdateFileBo();
  const { mutateAsync: deleteFile } = useDeleteFileByIdBo();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [sendViaApiByFileId, setSendViaApiByFileId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;

    setSendViaApiByFileId(
      Object.fromEntries(documentsExceptCustomInvoice.map((d) => [d.id, d.sendViaApi])),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sendViaApiSnapshotKey]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["expedition", "bo", expedition.id] });
  };

  const handleToggleIsPrivate = async (fileId: string, value: boolean) => {
    await updateFile({ id: fileId, payload: { isPrivate: value } });
    invalidate();
  };

  const handleDelete = async (fileId: string) => {
    await deleteFile(fileId);
    invalidate();
    toast.success(t("linkFileModal.deleteSuccess"));
  };

  const handleSave = async () => {
    const updates = documentsExceptCustomInvoice.filter(
      (d) => sendViaApiByFileId[d.id] !== d.sendViaApi,
    );

    if (updates.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(
        updates.map((d) =>
          updateFile({ id: d.id, payload: { sendViaApi: sendViaApiByFileId[d.id] } }),
        ),
      );
      await invalidate();
      toast.success(t("linkFileModal.saveSuccess"));
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t("linkFileModal.title")}
        description={t("linkFileModal.description")}
        size="lg"
        footer={
          <>
            <Button variant="outline" type="button" onClick={onClose} disabled={isSaving}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="animate-spin" /> : t("linkFileModal.submit")}
            </Button>
          </>
        }
      >
        <div className="flex w-full min-w-0 flex-col gap-3">
          {documentsExceptCustomInvoice.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">{t("linkFileModal.empty")}</p>
          ) : (
            documentsExceptCustomInvoice.map((document) => {
              const isLinked = sendViaApiByFileId[document.id] ?? document.sendViaApi;

              return (
                <div key={document.id} className="flex w-full min-w-0 flex-row items-center gap-3">
                  <Checkbox
                    className="shrink-0"
                    checked={isLinked}
                    onCheckedChange={(checked) =>
                      setSendViaApiByFileId((prev) => ({
                        ...prev,
                        [document.id]: checked === true,
                      }))
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <ExpeditionDocumentCard
                      file={{ ...document, sendViaApi: isLinked }}
                      onToggleIsPrivate={handleToggleIsPrivate}
                      onDelete={handleDelete}
                    />
                  </div>
                </div>
              );
            })
          )}
          <Button
            type="button"
            variant="ghost"
            className="h-auto self-start p-0 text-blue-600 no-underline hover:bg-transparent hover:text-blue-700"
            onClick={() => setUploadModalOpen(true)}
          >
            <Plus className="mr-1 size-4" />
            {t("linkFileModal.addFile")}
          </Button>
        </div>
      </Modal>
      <UploadFileBoModal
        expedition={expedition}
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        expeditionDocumentsId={expedition.expeditionDocuments?.id}
        expeditionId={expedition.id}
        allowedFileTypes={getCreatableFileTypes(FileAction.BO_FINALIZE_EXPEDITION)}
        invalidateQueryKey={["expedition", "bo", expedition.id]}
        defaultOptions={{
          isPrivate: true,
          sendViaApi: true,
        }}
      />
    </>
  );
};
